import { useCallback, useEffect, useMemo, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { Heart, Loader2 } from 'lucide-react';
import {
  createDonationCheckoutSession,
  type DonationFrequency,
  type DonorWallConsentLevel,
} from '@/lib/stripe-billing';
import { DonateTrustStrip } from '@/components/donate/DonateTrustStrip';
import { pushAnalyticsEvent } from '@/lib/analytics';
import {
  defaultAmountsForDonationFrequency,
  donationFrequencyFromHash,
  donationFrequencyFromSearch,
  shouldScrollDonationWidget,
} from '@/lib/donate-nav-links';

function readUtmParams(): Record<string, string> {
  const params = new URLSearchParams(globalThis.location.search);
  return {
    utmSource: params.get('utm_source') ?? '',
    utmMedium: params.get('utm_medium') ?? '',
    utmCampaign: params.get('utm_campaign') ?? '',
    utmTerm: params.get('utm_term') ?? '',
    utmContent: params.get('utm_content') ?? '',
  };
}

function frequencyFromRoute(hash: string, search: string): DonationFrequency {
  return donationFrequencyFromHash(hash) ?? donationFrequencyFromSearch(search) ?? 'monthly';
}

export interface DonatePageProps {
  /** Set when a non-anonymous user is signed in — pre-fills Stripe Checkout email. */
  readonly donorEmail?: string;
  readonly donorDisplayName?: string;
}

export function DonatePage({ donorEmail, donorDisplayName }: DonatePageProps): JSX.Element {
  const location = useLocation();
  const initialFrequency = frequencyFromRoute(location.hash, location.search);
  const initialAmounts = defaultAmountsForDonationFrequency(initialFrequency);
  const [frequency, setFrequency] = useState<DonationFrequency>(initialFrequency);
  const [selectedAmount, setSelectedAmount] = useState(initialAmounts.selectedAmount);
  const [customAmountInput, setCustomAmountInput] = useState(initialAmounts.customAmountInput);
  const [checkoutLoading, setCheckoutLoading] = useState(false);
  const [checkoutError, setCheckoutError] = useState<string | null>(null);
  const [donorWallConsent, setDonorWallConsent] = useState<DonorWallConsentLevel>('firstNameOnly');
  const [donorCountry, setDonorCountry] = useState('');

  const quickAmounts = useMemo(
    () => ({
      one_time: [25, 50, 100, 250],
      monthly: [10, 25, 50, 100],
    }),
    []
  );

  const activeQuickAmounts = frequency === 'monthly' ? quickAmounts.monthly : quickAmounts.one_time;

  const applyFrequency = useCallback((nextFrequency: DonationFrequency): void => {
    const defaults = defaultAmountsForDonationFrequency(nextFrequency);
    setFrequency(nextFrequency);
    setSelectedAmount(defaults.selectedAmount);
    setCustomAmountInput(defaults.customAmountInput);
  }, []);

  // React Router uses history.pushState for in-app links; hashchange does not fire.
  useEffect(() => {
    const routeFrequency =
      donationFrequencyFromHash(location.hash) ?? donationFrequencyFromSearch(location.search);
    if (routeFrequency) {
      applyFrequency(routeFrequency);
    }
    if (shouldScrollDonationWidget(location.hash)) {
      const widget = document.getElementById('donationwidget');
      if (widget) {
        requestAnimationFrame(() => {
          widget.scrollIntoView({ behavior: 'smooth', block: 'start' });
        });
      }
    }
  }, [location.hash, location.search, applyFrequency]);

  const startCheckout = async (amountInDollars: number): Promise<void> => {
    if (!Number.isFinite(amountInDollars) || amountInDollars < 1) {
      setCheckoutError('Donation amount must be at least $1.');
      return;
    }

    setCheckoutLoading(true);
    setCheckoutError(null);

    try {
      const successUrl = `${globalThis.location.origin}/donate/thank-you?checkout=success&session_id={CHECKOUT_SESSION_ID}`;
      const cancelUrl = `${globalThis.location.origin}/donate?checkout=cancel`;
      const utm = readUtmParams();
      const campaign = new URLSearchParams(globalThis.location.search).get('campaign') ?? '';
      const ambassador = new URLSearchParams(globalThis.location.search).get('ambassador') ?? '';
      const amountInCents = Math.round(amountInDollars * 100);

      pushAnalyticsEvent({
        event: 'donation_checkout_started',
        donation_frequency: frequency,
        donation_amount: amountInDollars,
        campaign_id: campaign,
        ambassador_id: ambassador,
      });

      const result = await createDonationCheckoutSession({
        amount: amountInCents,
        donationFrequency: frequency,
        successUrl,
        cancelUrl,
        ...(donorEmail?.trim() ? { donorEmail: donorEmail.trim() } : {}),
        ...(donorDisplayName?.trim() ? { donorName: donorDisplayName.trim() } : {}),
        metadata: {
          campaignId: campaign,
          campaignName: campaign || (frequency === 'monthly' ? 'Monthly donation' : 'General donation'),
          ambassadorId: ambassador,
          ambassadorName: ambassador,
          sourcePage: '/donate',
          donationFrequency: frequency,
          donorWallConsent,
          donorCountry: donorCountry.trim(),
          ...utm,
        },
      });

      globalThis.location.href = result.sessionUrl;
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Unable to start checkout right now.';
      setCheckoutError(message);
    } finally {
      setCheckoutLoading(false);
    }
  };

  const parsedCustomAmount = Number(customAmountInput);
  const isCustomAmountValid = Number.isFinite(parsedCustomAmount) && parsedCustomAmount >= 1;

  return (
    <main className="max-w-6xl mx-auto px-4 py-10 space-y-8" id="donationwidget">
      <section className="rounded-2xl border border-gray-200 bg-white p-6">
        <p className="text-sm font-semibold uppercase tracking-wide text-blue-700">Donate</p>
        <h1 className="text-3xl font-bold text-gray-900 mt-2">Support CKD warriors and families</h1>
        <p className="text-gray-600 mt-3 max-w-3xl">
          Choose a one-time or recurring donation to support emergency aid, patient stories, and
          long-term kidney care programs.
        </p>
      </section>

      <div className="grid grid-cols-1 gap-6 lg:grid-cols-[minmax(0,1fr)_280px] lg:items-start">
      <section className="rounded-2xl border border-gray-200 bg-white p-6">
        <div className="inline-flex rounded-lg border border-gray-200 p-1">
          <button
            type="button"
            onClick={() => applyFrequency('one_time')}
            className={`rounded-md px-4 py-2 text-sm font-semibold ${
              frequency === 'one_time' ? 'bg-blue-600 text-white' : 'text-gray-700 hover:bg-gray-50'
            }`}
          >
            One-time
          </button>
          <button
            type="button"
            onClick={() => applyFrequency('monthly')}
            className={`rounded-md px-4 py-2 text-sm font-semibold ${
              frequency === 'monthly' ? 'bg-blue-600 text-white' : 'text-gray-700 hover:bg-gray-50'
            }`}
          >
            Monthly
          </button>
        </div>

        <p className="mt-4 text-sm text-gray-600">
          {frequency === 'monthly'
            ? 'Build consistent monthly support for CKD warriors with secure recurring checkout.'
            : 'Make a one-time contribution to support urgent CKD care and recovery.'}
        </p>

        <div className="mt-5 grid grid-cols-2 gap-3 sm:grid-cols-4">
          {activeQuickAmounts.map((amount) => {
            const isActive = selectedAmount === amount && customAmountInput === String(amount);
            return (
              <button
                key={`${frequency}-${amount}`}
                type="button"
                onClick={() => {
                  setSelectedAmount(amount);
                  setCustomAmountInput(String(amount));
                }}
                disabled={checkoutLoading}
                className={`rounded-lg border px-3 py-2 text-sm font-semibold disabled:opacity-60 ${
                  isActive
                    ? 'border-blue-600 bg-blue-50 text-blue-700'
                    : 'border-gray-300 bg-white text-gray-800 hover:bg-gray-50'
                }`}
              >
                ${amount}
                {frequency === 'monthly' ? '/mo' : ''}
              </button>
            );
          })}
        </div>

        <div className="mt-4 flex flex-col gap-3 sm:flex-row sm:items-center">
          <label className="text-sm font-medium text-gray-700" htmlFor="donation-amount">
            Custom amount (USD)
          </label>
          <input
            id="donation-amount"
            inputMode="decimal"
            type="number"
            min={1}
            step="1"
            value={customAmountInput}
            onChange={(event) => {
              setCustomAmountInput(event.target.value);
              const nextAmount = Number(event.target.value);
              if (Number.isFinite(nextAmount) && nextAmount >= 1) {
                setSelectedAmount(nextAmount);
              }
            }}
            className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm text-gray-900 sm:w-40"
          />
          <button
            type="button"
            onClick={() => startCheckout(parsedCustomAmount)}
            disabled={!isCustomAmountValid || checkoutLoading}
            className="inline-flex items-center justify-center gap-2 rounded-lg bg-blue-600 px-4 py-2 text-sm font-semibold text-white hover:bg-blue-700 disabled:opacity-60"
          >
            {checkoutLoading ? <Loader2 size={15} className="animate-spin" /> : <Heart size={15} />}
            {frequency === 'monthly'
              ? `Donate $${isCustomAmountValid ? parsedCustomAmount : selectedAmount} / month`
              : `Donate $${isCustomAmountValid ? parsedCustomAmount : selectedAmount}`}
          </button>
        </div>

        {frequency === 'monthly' ? (
          <p className="mt-3 text-sm text-blue-700">
            Secure recurring with Stripe. You can cancel anytime from your donor dashboard.
          </p>
        ) : null}

        <fieldset className="mt-6 rounded-lg border border-gray-200 bg-gray-50 px-4 py-3">
          <legend className="px-1 text-sm font-semibold text-gray-900">Donor wall visibility</legend>
          <p className="mt-1 text-xs text-gray-600">
            Choose how your gift may appear on the public donor wall (first name and country only — never full email).
          </p>
          <div className="mt-3 space-y-2 text-sm text-gray-800">
            <label className="flex items-start gap-2">
              <input
                type="radio"
                name="donor-wall-consent"
                value="firstNameOnly"
                checked={donorWallConsent === 'firstNameOnly'}
                onChange={() => setDonorWallConsent('firstNameOnly')}
                className="mt-0.5"
              />
              <span>Show my first name and country on the donor wall</span>
            </label>
            <label className="flex items-start gap-2">
              <input
                type="radio"
                name="donor-wall-consent"
                value="anonymous"
                checked={donorWallConsent === 'anonymous'}
                onChange={() => setDonorWallConsent('anonymous')}
                className="mt-0.5"
              />
              <span>Give anonymously (shown as &quot;A supporter from [country]&quot;)</span>
            </label>
          </div>
          <label className="mt-3 block text-sm font-medium text-gray-700" htmlFor="donor-country">
            Country (optional, helps personalize the donor wall)
          </label>
          <input
            id="donor-country"
            type="text"
            value={donorCountry}
            onChange={(event) => setDonorCountry(event.target.value)}
            placeholder="e.g. United States, UAE, Philippines"
            className="mt-1 w-full rounded-lg border border-gray-300 px-3 py-2 text-sm text-gray-900"
          />
        </fieldset>
      </section>

      <DonateTrustStrip />
      </div>

      {checkoutError ? (
        <div className="rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
          {checkoutError}
        </div>
      ) : null}
    </main>
  );
}
